import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import axios from "axios";
import { ArrowRight, Trophy, Sparkles, CalendarDays, Newspaper } from "lucide-react";
import { Seo } from "../components/Seo";
import { Marquee } from "../components/Marquee";
import { Reveal } from "../components/Reveal";
import { TEAMS, IMAGES, LINKS, HONOURS } from "../data/club";
import { FixturesStrip } from "../components/FixturesStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MARQUEE_ITEMS = [
  "Celebrating 100 Years · 1926–2026",
  "NTCL Div 4 North Champions 2026",
  "Promoted to Division 3",
  "U11 Divisional Champions",
  "ECB All Stars at West Park",
  "Women's Team Launching 2027",
  "Active North Tyneside Grant Secured",
  "Two Saturday XIs · Midweek · Sunday",
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const [news, setNews] = useState([]);
  useEffect(() => {
    axios.get(`${API}/news`).then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Seo
        title="Whitley Bay Cricket Club — West Park, North Tyneside"
        description="Whitley Bay Cricket Club at West Park. Two Saturday league sides, midweek and Sunday cricket, juniors from U9 to U15, girls' squads and ECB All Stars."
      />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-end overflow-hidden" data-testid="home-hero">
        <motion.div
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{ backgroundImage: "url(/assets/aerial-ground.webp)", y: bgY }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1520]/60 via-[#0B1520]/55 to-[#0B1520]" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1520]/70 to-transparent" aria-hidden="true" />

        <motion.div style={{ opacity: fade }} className="relative w-full max-w-7xl mx-auto px-5 sm:px-8 pb-20 sm:pb-28 pt-40">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xs font-mono-club uppercase tracking-[0.3em] text-[#E5AC3C] mb-6"
            data-testid="hero-eyebrow"
          >
            Est. 1926 · West Park · North Tyneside
          </motion.p>

          <h1 className="font-head font-black tracking-tight leading-[0.98] text-[13vw] sm:text-7xl lg:text-8xl" data-testid="hero-title">
            <span className="block overflow-hidden">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}>
                WHITLEY BAY
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.38, ease: [0.22, 1, 0.36, 1] }}>
                CRICKET CLUB
              </motion.span>
            </span>
            <span className="block overflow-hidden mt-2">
              <motion.span
                className="block font-serif-accent font-medium text-[#E5AC3C] text-[7vw] sm:text-4xl lg:text-5xl"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.52, ease: [0.22, 1, 0.36, 1] }}
              >
                Champions by the coast.
              </motion.span>
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-join"
              className="inline-flex items-center gap-2 bg-[#E5AC3C] hover:bg-[#F3C35B] text-[#0B1520] font-bold px-7 py-3.5 rounded-full transition-colors duration-200 shadow-[0_0_30px_rgba(229,172,60,0.3)]"
            >
              Join the Club <ArrowRight size={17} />
            </Link>
            <Link
              to="/teams"
              data-testid="hero-cta-teams"
              className="inline-flex items-center gap-2 border border-white/25 hover:border-[#E5AC3C] hover:text-[#E5AC3C] text-[#F7F5F0] font-semibold px-7 py-3.5 rounded-full transition-colors duration-200"
            >
              Explore our teams
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-10 inline-flex items-center gap-3 rounded-full border border-[#E5AC3C]/40 bg-[#0B1520]/60 backdrop-blur px-5 py-2.5"
            data-testid="hero-champions-ribbon"
          >
            <Trophy size={16} className="text-[#E5AC3C]" />
            <span className="text-xs sm:text-sm font-semibold tracking-wide text-[#F7F5F0]">
              1st XI — NTCL Division 4 North Champions 2026 · Promoted to Division 3
            </span>
          </motion.div>
        </motion.div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      {/* GRANT BANNER */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-16 sm:mt-24" data-testid="grant-section">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-[#E5AC3C]/30 bg-gradient-to-br from-[#122030] to-[#1B4D3E]/40 p-8 sm:p-12">
            <Sparkles className="absolute -top-4 -right-4 w-40 h-40 text-[#E5AC3C]/10" aria-hidden="true" />
            <p className="text-xs font-mono-club uppercase tracking-[0.25em] text-[#E5AC3C] mb-3">Announcement</p>
            <h2 className="font-head text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-2xl">
              Indoor winter practice, funded by <span className="font-serif-accent font-medium text-[#E5AC3C]">Active North Tyneside</span>
            </h2>
            <p className="mt-4 max-w-2xl text-[#9BB0C1] leading-relaxed">
              The club has secured a grant from Active North Tyneside to pay for indoor practice sessions for the newly formed women's team this winter.
              Venue and dates will be announced soon — members and new players alike will benefit.
            </p>
            <div className="mt-6 inline-flex items-center gap-3 rounded-xl bg-white px-4 py-3" data-testid="grant-partner-logo">
              <img src="/assets/active-north-tyneside.webp" alt="Active North Tyneside logo" className="h-10 w-auto" loading="lazy" />
              <span className="text-[10px] font-bold text-[#0B1520] uppercase tracking-[0.12em] leading-tight">Indoor practice funded by<br />Active North Tyneside</span>
            </div>
            <div>
              <Link
                to="/news"
                data-testid="grant-read-more-link"
                className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#E5AC3C] hover:text-[#F3C35B] transition-colors"
              >
                Read the announcement <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* HONOURS STRIP */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20 sm:mt-28" data-testid="home-honours">
        <Reveal>
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-xs font-mono-club uppercase tracking-[0.25em] text-[#E5AC3C] mb-3">A club on the up</p>
              <h2 className="font-head text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Season highlights</h2>
            </div>
            <Link to="/honours" className="hidden sm:inline-flex items-center gap-2 text-sm font-bold text-[#9BB0C1] hover:text-[#E5AC3C] transition-colors" data-testid="honours-view-all-link">
              All honours <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
        <div className="grid gap-5 md:grid-cols-3">
          {HONOURS.map((h, i) => (
            <Reveal key={h.title} delay={i * 0.1}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                className="h-full rounded-2xl border border-white/10 bg-[#122030] p-7 hover:border-[#E5AC3C]/40 transition-colors"
                data-testid={`honour-card-${i}`}
              >
                <span className="text-xs font-mono-club uppercase tracking-[0.2em] text-[#5C7285]">{h.year} · {h.team}</span>
                <h3 className="mt-3 font-head text-lg sm:text-xl font-bold leading-snug">{h.title}</h3>
                <p className="mt-3 text-sm text-[#9BB0C1] leading-relaxed">{h.detail}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TEAM PATHWAYS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20 sm:mt-28" data-testid="home-teams">
        <Reveal>
          <p className="text-xs font-mono-club uppercase tracking-[0.25em] text-[#E5AC3C] mb-3">One club, every pathway</p>
          <h2 className="font-head text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-xl">
            From All Stars to the <span className="font-serif-accent font-medium text-[#E5AC3C]">First XI</span>
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {TEAMS.map((t, i) => (
            <Reveal key={t.slug} delay={(i % 3) * 0.08}>
              <Link to={`/teams/${t.slug}`} data-testid={`team-card-${t.slug}`}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  className="group h-full rounded-2xl border border-white/10 bg-[#122030] overflow-hidden hover:border-[#E5AC3C]/40 transition-colors"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={t.image}
                      alt={`${t.name} cricket at Whitley Bay Cricket Club`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-head text-lg font-bold">{t.name}</h3>
                      <span className="text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#E5AC3C] border border-[#E5AC3C]/30 rounded-full px-2.5 py-1 whitespace-nowrap">
                        {t.group}
                      </span>
                    </div>
                    <p className="mt-1 text-xs font-mono-club uppercase tracking-[0.15em] text-[#5C7285]">{t.league}</p>
                    <p className="mt-3 text-sm text-[#9BB0C1]">{t.tagline}</p>
                  </div>
                </motion.div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ALL STARS / COASTAL SPLIT */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20 sm:mt-28 grid gap-10 lg:grid-cols-2 items-center" data-testid="home-allstars">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden border border-white/10">
            <img src={IMAGES.juniors} alt="Junior cricketers at an All Stars Friday session at West Park" className="w-full h-[420px] object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1520]/80 to-transparent" aria-hidden="true" />
            <div className="absolute bottom-5 left-5 right-5">
              <span className="text-xs font-mono-club uppercase tracking-[0.2em] text-[#E5AC3C]">Ages 5–8 · Summer at West Park</span>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-xs font-mono-club uppercase tracking-[0.25em] text-[#E5AC3C] mb-3">ECB All Stars</p>
          <h2 className="font-head text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
            First runs, <span className="font-serif-accent font-medium text-[#E5AC3C]">lifelong love</span> of the game
          </h2>
          <p className="mt-5 text-[#9BB0C1] leading-relaxed">
            We run the ECB All Stars programme throughout the summer — fun, safe sessions giving children aged 5 to 8
            their first taste of cricket. The pavilion stays open for parents, and many All Stars go on to join our U9s.
          </p>
          <div className="mt-7 flex flex-wrap gap-4">
            <Link to="/teams/juniors" data-testid="allstars-juniors-link" className="inline-flex items-center gap-2 bg-[#E5AC3C] hover:bg-[#F3C35B] text-[#0B1520] font-bold px-6 py-3 rounded-full text-sm transition-colors">
              Junior cricket at WBCC <ArrowRight size={15} />
            </Link>
            <Link to="/social" data-testid="allstars-social-link" className="inline-flex items-center gap-2 border border-white/20 hover:border-[#E5AC3C] hover:text-[#E5AC3C] font-semibold px-6 py-3 rounded-full text-sm transition-colors">
              Social scene
            </Link>
          </div>
        </Reveal>
      </section>

      {/* SOCIAL / PAVILION */}
      <section className="relative mt-20 sm:mt-28 py-20 sm:py-28 overflow-hidden" data-testid="home-social">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${IMAGES.clubhouse})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1520] via-[#0B1520]/80 to-[#0B1520]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid gap-10 lg:grid-cols-2 items-center">
          <Reveal>
            <p className="text-xs font-mono-club uppercase tracking-[0.25em] text-[#E5AC3C] mb-3">More than a cricket club</p>
            <h2 className="font-head text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
              Good cricket. <span className="font-serif-accent font-medium text-[#E5AC3C]">Great company.</span>
            </h2>
            <p className="mt-5 text-[#9BB0C1] leading-relaxed max-w-lg">
              The bar at West Park is the social heart of the club — post-match drinks, summer BBQs, presentation
              nights and a warm welcome for members, opponents and families alike.
            </p>
            <Link to="/social" data-testid="home-social-link" className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-[#E5AC3C] hover:text-[#F3C35B] transition-colors">
              Social & bar at West Park <ArrowRight size={15} />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 gap-4">
              {[
                ["2", "Saturday league sides"],
                ["7", "Teams from U9 to seniors"],
                ["1926", "The year it all began"],
                ["2027", "Women's team launches"],
              ].map(([num, label]) => (
                <div key={label} className="rounded-2xl border border-white/10 bg-[#0B1520]/70 backdrop-blur p-6" data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="font-head text-3xl sm:text-4xl font-black text-[#E5AC3C]">{num}</div>
                  <div className="mt-2 text-xs sm:text-sm text-[#9BB0C1]">{label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* LATEST NEWS */}
      {news.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20 sm:mt-28" data-testid="home-news">
          <Reveal>
            <div className="flex items-end justify-between gap-6 mb-10">
              <div>
                <p className="text-xs font-mono-club uppercase tracking-[0.25em] text-[#E5AC3C] mb-3">From the club</p>
                <h2 className="font-head text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Latest news</h2>
              </div>
              <Link to="/news" className="hidden sm:inline-flex items-center gap-2 text-sm font-bold text-[#9BB0C1] hover:text-[#E5AC3C] transition-colors" data-testid="news-view-all-link">
                All news <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-3">
            {news.map((n, i) => (
              <Reveal key={n.id} delay={i * 0.1}>
                <Link to="/news" data-testid={`home-news-card-${i}`}>
                  <div className="h-full rounded-2xl border border-white/10 bg-[#122030] p-7 hover:border-[#E5AC3C]/40 transition-colors">
                    <span className="inline-flex items-center gap-2 text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#E5AC3C]">
                      <Newspaper size={12} /> {n.category}
                    </span>
                    <h3 className="mt-3 font-head text-lg font-bold leading-snug">{n.title}</h3>
                    <p className="mt-3 text-sm text-[#9BB0C1] leading-relaxed line-clamp-3">{n.summary}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#5C7285]">
                      <CalendarDays size={12} /> {n.date}
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* UPCOMING FIXTURES STRIP */}
      <FixturesStrip />

      {/* CTA BANNER */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 my-20 sm:my-28" data-testid="home-cta">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-[#E5AC3C]/30 p-10 sm:p-16">
            <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${IMAGES.lighthouse})` }} aria-hidden="true" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B1520]/90 to-[#0B1520]/60" aria-hidden="true" />
            <div className="relative max-w-2xl">
              <h2 className="font-head text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
                Play, watch or celebrate — <span className="font-serif-accent font-medium text-[#E5AC3C]">West Park is open</span>
              </h2>
              <p className="mt-4 text-[#9BB0C1] leading-relaxed">
                Book the clubhouse for your event, join a team, or follow every ball on Play-Cricket.
              </p>
              <div className="mt-7 flex flex-wrap gap-4">
                <Link to="/booking" data-testid="cta-booking-link" className="inline-flex items-center gap-2 bg-[#E5AC3C] hover:bg-[#F3C35B] text-[#0B1520] font-bold px-6 py-3 rounded-full text-sm transition-colors">
                  Book a facility <ArrowRight size={15} />
                </Link>
                <a href={LINKS.playCricket} target="_blank" rel="noopener noreferrer" data-testid="cta-playcricket-link" className="inline-flex items-center gap-2 border border-white/25 hover:border-[#E5AC3C] hover:text-[#E5AC3C] font-semibold px-6 py-3 rounded-full text-sm transition-colors">
                  Fixtures on Play-Cricket
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
